import type { CSSProperties } from 'react';
import { LABEL_COLOR_TOKENS, isLabelHexColor, labelTextColorOn, type LabelColorToken } from '@infras/shared-types';
import { Icon } from '../Icon';
import '../../styles/labels.css';

/** A label as every lane passes it around. `id` is the GraphQL Int as a string. */
export type LabelLite = { id: string; name: string; color: string; scope: string | null; system?: boolean };

/** The named tokens (shared-types `label-color`, also core `LABEL_COLORS`). Each has one
 *  `--tag-<color>-bg/fg` pair in globals.css, used on every page so a label is one colour.
 *  A label may instead carry a custom lowercase `#rrggbb` — see `labelColorProps`. */
export const LABEL_COLORS = LABEL_COLOR_TOKENS;
export type LabelColor = LabelColorToken;

export const labelColorOf = (c: string | null | undefined): LabelColor =>
  (LABEL_COLORS as readonly string[]).includes(c ?? '') ? (c as LabelColor) : 'gray';

/** Class + inline vars for any surface that paints a label colour (chip, board bar, swatch).
 *  A token → `label-chip--<token>` (its CSS variables). A custom `#rrggbb` → `label-chip--custom`
 *  with the hex as the background and black or white text, whichever has the higher WCAG
 *  contrast — the same in light and dark themes. Anything unknown → gray. */
export function labelColorProps(c: string | null | undefined): { className: string; style?: CSSProperties } {
  if (isLabelHexColor(c)) {
    const fg = labelTextColorOn(c);
    return {
      className: 'label-chip--custom',
      style: { '--chip-bg': c, '--chip-fg': fg } as CSSProperties,
    };
  }
  return { className: `label-chip--${labelColorOf(c)}` };
}

/** Scope = everything before the LAST `::` (same rule as core `scopeOf`); null for a plain label. */
export function splitLabel(name: string, scope?: string | null): { scope: string | null; value: string } {
  const i = name.lastIndexOf('::');
  if (i <= 0 || i + 2 >= name.length) return { scope: null, value: name };
  return { scope: scope ?? name.slice(0, i), value: name.slice(i + 2) };
}

/** GitLab-style chip: a scoped label `scope::value` renders as two halves (filled scope,
 *  tinted value); a plain label is one tinted pill. Colours come from `--tag-<color>-*` (or the
 *  inline custom-hex vars from `labelColorProps`) and are the same on every page, board included. */
export function LabelChip(props: {
  label: LabelLite;
  onRemove?: () => void;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { label, onRemove, size = 'md' } = props;
  const color = labelColorProps(label.color);
  const { scope, value } = splitLabel(label.name, label.scope);
  const cls = `tag-badge label-chip ${color.className} label-chip--${size}${scope ? ' label-chip--scoped' : ''}`;
  return (
    <span className={cls} style={color.style} title={label.name}>
      {scope ? (
        <>
          <span className="label-chip__scope">{scope}</span>
          <span className="label-chip__value">{value}</span>
        </>
      ) : (
        <span className="label-chip__value">{value}</span>
      )}
      {onRemove ? (
        <button
          type="button"
          className="label-chip__remove"
          aria-label={`Remove label ${label.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Icon name="close" size={12} />
        </button>
      ) : null}
    </span>
  );
}
